import { useMutation, useQuery } from '@apollo/client';
import { AutoCompleteTalentSpecialties } from '@components/AutoComplete/AutoCompleteTalentSpecialties';
import FormContainer from '@components/FormContainer';
import { getTalentSpecialtiesQuery } from '@gql/talentSpecialties';
import { Stack } from '@mui/material';
import { groupBy } from 'lodash';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GET_TALENT, UPDATE_PREFERENCES_TALENT } from '@gql/talents';
// import { getSelfTalentQuery, updatePreferencesTalent } from './queries';

type Props = {
    talent?: any;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
    onBack?: () => void;
    isEdit?: boolean;
};

const TalentPreferencesForm = React.forwardRef((props: Props, ref) => {
    const { talent } = React.useMemo(() => {
        const talent = props.talent;
        return { talent };
    }, [props.talent]);

    const { data: dataSpecialities } = useQuery(getTalentSpecialtiesQuery);

    const { showSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);

    const defaultValues = React.useMemo(
        () => ({
            majors: talent?.majors ?? [],
        }),
        [talent?.majors],
    );

    const {
        control,
        handleSubmit,
        getValues,
        trigger,
        reset,
        formState: { isDirty },
    } = useForm({
        mode: 'all',
        defaultValues,
    });

    const [update] = useMutation(UPDATE_PREFERENCES_TALENT, {
        onCompleted: () => {
            showSnackbar('Savoir-faire mis à jour avec succès', 'success');
            props?.onSuccess?.(getValues());
            setLoading(false);
            setFormState('view');
        },
        onError: error => {
            props?.onError?.(error);
            setLoading(false);
        },
    });

    const submitHandler = useCallback(
        async data => {
            setLoading(true);
            await update({
                variables: {
                    payload: {
                        // Keep professions in this payload to satisfy the existing GraphQL contract.
                        professions: talent?.professions ?? [],
                        majors: data.majors,
                    },
                    talentId: talent?._id,
                },
                refetchQueries: () => [
                    {
                        query: GET_TALENT,
                        variables: { talentId: talent?._id }
                    },
                ],
            });
        },
        [update, talent],
    );
    const [formState, setFormState] = useState<'view' | 'edit'>('view');

    const groupedSpecialties = groupBy(dataSpecialities?.specialities ?? [], 'groupName');

    // register trigger so it can triggered by parent component
    React.useImperativeHandle(ref, () => ({
        submit: async () => {
            const isValid = await trigger();
            if (isValid && isDirty) {
                await handleSubmit(submitHandler)();
            }
            return isValid;
        },
    }));

    // Reset the form when talent changes.
    React.useEffect(() => {
        if (talent) {
            reset({
                majors: talent.majors || [],
            });
        }
    }, [talent, reset]);

    return (
        <FormContainer
            onBack={props.onBack}
            loading={loading}
            formState={formState}
            setFormState={setFormState}
            onCancel={() => {
                reset(defaultValues);
            }}
            onSubmit={handleSubmit(submitHandler)}
            formTitle="Savoir-faire">
            <Stack>
                <AutoCompleteTalentSpecialties
                    allowAdd
                    viewOnly={formState === 'view'}
                    placeholder={'Sélectionnez votre savoir-faire'}
                    control={control}
                    name="majors"
                    groupedOptions={Object.keys(groupedSpecialties).map(groupName => ({
                        name: groupName,
                        children: groupedSpecialties[groupName].map(item => ({
                            id: item._id,
                            name: item.name,
                            value: item._id,
                        })),
                    }))}
                />
            </Stack>
        </FormContainer>
    );
});

export default TalentPreferencesForm;
